// The office's answer to a report, in the installer's words. One line, a tone,
// never the process (no action ids, no case ids, no "executed").
import { useCallback, useState } from "react";
import type { ProposalResult } from "@artemis-group/aiw-contracts";
import { Check, Clock, X } from "lucide-react";
import { cn } from "@aiw/ui";
import { useT } from "../i18n";
import { answerWords } from "../lib/report";

export interface Answer { text: string; tone: "ok" | "wait" | "bad"; at: string }

export function useAnswer() {
  const { t } = useT();
  const [answer, setAnswer] = useState<Answer | null>(null);
  const say = useCallback((r: ProposalResult | "failed") => {
    const w = answerWords(r);
    const a: Answer = { text: t(w.key, w.vars), tone: w.tone, at: new Date().toISOString() };
    setAnswer(a);
    return a;
  }, [t]);
  return { answer, say, clear: () => setAnswer(null) };
}

export function AnswerLine({ answer, className }: { answer: Answer | null; className?: string }) {
  if (!answer) return null;
  const Icon = answer.tone === "ok" ? Check : answer.tone === "wait" ? Clock : X;
  return (
    <p role="status" className={cn("flex items-start gap-2 rounded-md border px-3 py-2 text-sm", answer.tone === "ok" && "border-success/40 bg-success/10 text-success", answer.tone === "wait" && "border-primary/30 bg-accent text-accent-foreground", answer.tone === "bad" && "border-destructive/40 bg-destructive/10 text-destructive", className)}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <span>{answer.text}</span>
    </p>
  );
}
